'use client'

import { useAuth } from '@renderer/chat/providers'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { useGlobalEvent, GlobalEvents, GlobalStore } from '@highlight/ipc'

import { PAGINATION_LIMIT } from '@renderer/utils/constants'
import { ChatHistoryItem, v1Client } from '@renderer/chat'
import { useChatHistoryStore } from '@renderer/stores/chat-history'
import { CHAT_HISTORY_QUERY_KEY } from '@renderer/stores/conversation-query-keys'
import { useSetSidePanelOpen, selectedChatIdAtom } from '@renderer/stores/side-panel'
import { useMemo } from 'react'

const RETRY_ATTEMPTS = 5

function useHistory(opts?: { limit?: number }) {
  const { getAuthToken } = useAuth()
  const { invalidateChatHistory, addOrUpdateChat } = useChatHistoryStore()

  // Listen for chat:message:sent events from @renderer/chat
  useGlobalEvent('chat:message:sent', async () => {
    // Invalidate the chat history to ensure it's fresh
    await invalidateChatHistory()
  })

  // Optimistically add new chats to history when the backend assigns a real ID
  useGlobalEvent('chat:created', async (data: { chatId: string }) => {
    await addOrUpdateChat({ id: data.chatId })
  })

  const query = useInfiniteQuery({
    queryKey: [CHAT_HISTORY_QUERY_KEY],
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      const response = await v1Client.GET('/conversations', {
        params: {
          query: {
            limit: opts?.limit ?? PAGINATION_LIMIT,
            nextCursor: pageParam,
          },
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Network response was not ok')
      }

      const data = response.data

      return {
        data: data.data,
        nextCursor: data.nextCursor,
      }
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: Infinity,
  })

  const chats = useMemo(() => {
    return query.data?.pages.flatMap((page) => page.data) ?? []
  }, [query.data])

  return useMemo(() => {
    return {
      ...query,
      chats,
    }
  }, [chats, query])
}

/**
 * `enabled` lets a caller withhold the request while `chatId` is a
 * client-minted id the backend hasn't confirmed yet (a fresh chat's temp
 * UUID before the `metadata` SSE event swaps in the real one) — querying it
 * is a guaranteed 404, not a transient miss, so it isn't worth the retries.
 */
function useHistoryByChatId(chatId: string | undefined, options?: { enabled?: boolean }) {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: ['chat', chatId],
    queryFn: async () => {
      if (!chatId) return

      const response = await v1Client.GET('/conversations/{id}', {
        params: {
          path: {
            id: chatId,
          },
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        console.log(response.error)
        throw new Error('Network response was not ok')
      }

      const data = response.data.data

      if (!data.title) {
        throw new Error('Title not found. Refetching...')
      }

      return data
    },
    enabled: !!chatId && (options?.enabled ?? true),
    gcTime: 60000,
    retry: RETRY_ATTEMPTS,
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 2s, 4s, 8s, 10s, 10s
      return Math.min(2000 * 2 ** attemptIndex, 10000)
    },
  })
}

function useRefetchChatById() {
  const { getAuthToken } = useAuth()
  const { addOrUpdateChat, getHistory } = useChatHistoryStore()

  return useMutation({
    mutationKey: ['chat-history-by-id'],
    mutationFn: async (chatId: string | undefined) => {
      if (!chatId) return

      const chatHistory = getHistory()
      const existingChat = chatHistory.find((chat) => chat.id === chatId)

      if (existingChat && !!existingChat.title) {
        return existingChat
      }

      const response = await v1Client.GET('/conversations/{id}', {
        params: {
          path: {
            id: chatId,
          },
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        console.log(response.error)
        throw new Error('Network response was not ok')
      }

      const data = response.data.data
      addOrUpdateChat(data)

      if (!data.title) {
        throw new Error('Title not found. Refetching...')
      }

      return data
    },
    gcTime: 60000,
    retry: RETRY_ATTEMPTS,
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 2s, 4s, 8s, 10s, 10s
      return Math.min(2000 * 2 ** attemptIndex, 10000)
    },
  })
}

function useDeleteChat() {
  const { getAuthToken } = useAuth()
  const { removeChatsByIds } = useChatHistoryStore()
  const [selectedChatId, setSelectedChatId] = useAtom(selectedChatIdAtom)
  const setSidePanelOpen = useSetSidePanelOpen()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-chat'],
    mutationFn: async (chatId: ChatHistoryItem['id']) => {
      if (!chatId) return

      const response = await v1Client.DELETE('/conversations/{id}', {
        params: {
          path: {
            id: chatId,
          },
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Network response was not ok')
      }

      return chatId
    },
    onMutate: async (chatId: ChatHistoryItem['id']) => {
      if (!chatId) return

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [CHAT_HISTORY_QUERY_KEY] })

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData([CHAT_HISTORY_QUERY_KEY])

      // Optimistically update the cache by removing the deleted chat
      queryClient.setQueryData([CHAT_HISTORY_QUERY_KEY], (old: any) => {
        if (!old) return old

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((chat: ChatHistoryItem) => chat.id !== chatId),
          })),
        }
      })

      // Return a context object with the snapshotted value
      return { previousChats }
    },
    onSuccess: (chatId) => {
      removeChatsByIds([chatId as string])

      if (selectedChatId === chatId) {
        setSidePanelOpen(false)
        setSelectedChatId('')
      }

      // Clean up preparation chat IDs from GlobalStore
      const store = GlobalStore.conversations()
      const allKeys = Object.keys(store.all())
      allKeys.forEach((key) => {
        if (key.startsWith('meeting.') && key.endsWith('.preparation')) {
          const data = store.get<{ chatId: string }>(key)
          if (data?.chatId === chatId) {
            store.delete(key)
          }
        }
      })

      // Emit event to notify @renderer/chat to invalidate history
      GlobalEvents.emit('chat:history:invalidate')

      console.log('Deleted chat:', chatId)
    },
    onError: (error, chatId, context) => {
      // Rollback the optimistic update
      if (context?.previousChats) {
        queryClient.setQueryData([CHAT_HISTORY_QUERY_KEY], context.previousChats)
      }

      console.error('Failure to delete chat:', chatId, error)
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache consistency
      queryClient.invalidateQueries({ queryKey: [CHAT_HISTORY_QUERY_KEY] })
    },
  })
}

function useBulkDeleteChats() {
  const { getAuthToken } = useAuth()
  const { removeChatsByIds } = useChatHistoryStore()
  const [selectedChatId, setSelectedChatId] = useAtom(selectedChatIdAtom)
  const setSidePanelOpen = useSetSidePanelOpen()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-chat'],
    mutationFn: async (chatIds: Array<ChatHistoryItem['id']>) => {
      if (!chatIds || chatIds.length === 0) return

      const response = await v1Client.DELETE('/conversations', {
        body: {
          conversationIds: chatIds,
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Network response was not ok')
      }

      return chatIds
    },
    onMutate: async (chatIds: Array<ChatHistoryItem['id']>) => {
      if (!chatIds || chatIds.length === 0) return

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [CHAT_HISTORY_QUERY_KEY] })

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData([CHAT_HISTORY_QUERY_KEY])

      // Optimistically update the cache by removing the deleted chats
      queryClient.setQueryData([CHAT_HISTORY_QUERY_KEY], (old: any) => {
        if (!old) return old

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((chat: ChatHistoryItem) => !chatIds.includes(chat.id)),
          })),
        }
      })

      // Return a context object with the snapshotted value
      return { previousChats }
    },
    onSuccess: (chatIds) => {
      if (!chatIds || chatIds.length === 0) return

      removeChatsByIds(chatIds)

      // Remove from chat tabs and pinned tabs
      for (const chatId of chatIds) {
        if (chatId === selectedChatId) {
          setSidePanelOpen(false)
          setSelectedChatId('')
        }

        // Clean up preparation chat IDs from GlobalStore
        const store = GlobalStore.conversations()
        const allKeys = Object.keys(store.all())
        allKeys.forEach((key) => {
          if (key.startsWith('meeting.') && key.endsWith('.preparation')) {
            const data = store.get<{ chatId: string }>(key)
            if (data?.chatId === chatId) {
              store.delete(key)
            }
          }
        })
      }

      // Emit event to notify @renderer/chat to invalidate history
      GlobalEvents.emit('chat:history:invalidate')

      console.log('Bulk deleted chats:', chatIds)
    },
    onError: (error, chatIds, context) => {
      // Rollback the optimistic update
      if (context?.previousChats) {
        queryClient.setQueryData([CHAT_HISTORY_QUERY_KEY], context.previousChats)
      }

      console.error('Failure to bulk delete chats:', chatIds, error)
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache consistency
      queryClient.invalidateQueries({ queryKey: [CHAT_HISTORY_QUERY_KEY] })
    },
  })
}

function useUpdateChatTitle() {
  const { getAuthToken } = useAuth()
  const { addOrUpdateChat } = useChatHistoryStore()

  return useMutation({
    mutationKey: ['update-chat-title'],
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const response = await v1Client.PUT('/conversations/{id}', {
        params: { path: { id } },
        body: { title },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error(String(response.error.error) || 'Failed to update chat title')
      }

      return response.data.data
    },
    onSuccess: (data) => {
      if (data) {
        addOrUpdateChat(data)
      }
    },
    onError: (error) => {
      console.error('Failed to update chat title:', error)
    },
  })
}

export { useHistory, useHistoryByChatId, useRefetchChatById, useDeleteChat, useBulkDeleteChats, useUpdateChatTitle }
